import {
  Action,
  Cancel,
  Content,
  Description,
  Overlay,
  Portal,
  Root,
  Title,
  Trigger,
} from '@radix-ui/react-alert-dialog'
import { type ComponentPropsWithoutRef, type ElementRef, type HTMLAttributes, type RefObject } from 'react'

import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const AlertDialog = Root

const AlertDialogTrigger = Trigger

const AlertDialogPortal = Portal

const AlertDialogOverlay = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof Overlay> & { ref?: RefObject<ElementRef<typeof Overlay> | null> }) => (
  <Overlay
    className={cn(
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80 data-[state=closed]:animate-out data-[state=open]:animate-in',
      className
    )}
    {...props}
    ref={ref}
  />
)
AlertDialogOverlay.displayName = Overlay.displayName

const AlertDialogContent = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof Content> & { ref?: RefObject<ElementRef<typeof Content> | null> }) => (
  <AlertDialogPortal>
    <AlertDialogOverlay />
    <Content
      className={cn(
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed top-[50%] left-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=closed]:animate-out data-[state=open]:animate-in sm:rounded-lg',
        className
      )}
      ref={ref}
      {...props}
    />
  </AlertDialogPortal>
)
AlertDialogContent.displayName = Content.displayName

const AlertDialogHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-2 text-center sm:text-left', className)} {...props} />
)
AlertDialogHeader.displayName = 'AlertDialogHeader'

const AlertDialogFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />
)
AlertDialogFooter.displayName = 'AlertDialogFooter'

const AlertDialogTitle = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof Title> & { ref?: RefObject<ElementRef<typeof Title> | null> }) => (
  <Title className={cn('font-semibold text-lg', className)} ref={ref} {...props} />
)
AlertDialogTitle.displayName = Title.displayName

const AlertDialogDescription = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof Description> & { ref?: RefObject<ElementRef<typeof Description> | null> }) => (
  <Description className={cn('text-muted-foreground text-sm', className)} ref={ref} {...props} />
)
AlertDialogDescription.displayName = Description.displayName

const AlertDialogAction = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof Action> & { ref?: RefObject<ElementRef<typeof Action> | null> }) => (
  <Action className={cn(buttonVariants(), className)} ref={ref} {...props} />
)
AlertDialogAction.displayName = Action.displayName

const AlertDialogCancel = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof Cancel> & { ref?: RefObject<ElementRef<typeof Cancel> | null> }) => (
  <Cancel className={cn(buttonVariants({ variant: 'outline' }), 'mt-2 sm:mt-0', className)} ref={ref} {...props} />
)
AlertDialogCancel.displayName = Cancel.displayName

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
}
